import { useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { createCheckout } from "@/lib/api";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...props}>
    <path fill="#4285F4" d="M23.06 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h6.2a5.3 5.3 0 0 1-2.3 3.48v2.88h3.72c2.18-2 3.44-4.96 3.44-8.37Z" />
    <path fill="#34A853" d="M12 24c3.11 0 5.72-1.03 7.62-2.78l-3.72-2.88c-1.03.69-2.35 1.1-3.9 1.1-3 0-5.54-2.02-6.45-4.74H1.7v2.97A11.5 11.5 0 0 0 12 24Z" />
    <path fill="#FBBC05" d="M5.55 14.7a6.9 6.9 0 0 1 0-4.4V7.33H1.7a11.5 11.5 0 0 0 0 10.34l3.85-2.97Z" />
    <path fill="#EA4335" d="M12 4.75c1.69 0 3.21.58 4.4 1.72l3.3-3.3A11.5 11.5 0 0 0 1.7 7.33l3.85 2.97C6.46 6.77 9 4.75 12 4.75Z" />
  </svg>
);

const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M16.37 12.6c.03 3.1 2.72 4.13 2.75 4.15-.02.07-.43 1.47-1.42 2.92-.86 1.25-1.75 2.5-3.15 2.53-1.38.03-1.82-.82-3.4-.82-1.57 0-2.06.79-3.36.85-1.35.05-2.38-1.35-3.25-2.6-1.78-2.56-3.14-7.24-1.31-10.4.9-1.57 2.52-2.56 4.28-2.59 1.33-.02 2.58.9 3.4.9.8 0 2.33-1.11 3.92-.95.67.03 2.54.27 3.74 2.03-.1.06-2.23 1.3-2.2 3.9M13.9 4.4c.72-.88 1.21-2.1 1.08-3.32-1.04.04-2.3.69-3.05 1.56-.67.78-1.25 2.02-1.1 3.2 1.16.1 2.34-.59 3.07-1.44" />
  </svg>
);

const Perk = ({ children }) => (
  <li className="flex items-start gap-2.5 text-base text-foreground/90">
    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
    <span>{children}</span>
  </li>
);

export const PaywallModal = () => {
  const { user, login, loginApple, appleEnabled } = useAuth();
  const [busy, setBusy] = useState(false);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await createCheckout(window.location.origin);
      window.location.href = checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      className="pointer-events-none absolute inset-0 z-30 flex items-start justify-center px-4 pt-16 sm:pt-24"
    >
      <div className="pointer-events-auto w-full max-w-md animate-fade-up rounded-2xl border border-primary/40 bg-card/95 p-7 shadow-[0_20px_60px_rgba(0,0,0,0.6)] backdrop-blur-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/40">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-800 leading-tight text-foreground">Your report is ready</h2>
            <p className="text-sm text-muted-foreground">Unlock the full compliance breakdown below</p>
          </div>
        </div>

        <div className="mb-5 rounded-xl bg-secondary/60 p-4">
          <div className="flex items-baseline gap-1.5">
            <span className="font-heading text-5xl font-900 text-primary">$4.99</span>
            <span className="text-base font-600 text-foreground/80">/ unlimited scans*</span>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            *Unlimited scans for 15 days — a small fraction of the add-on fees a dealer may include in your
            deal. <span className="font-700 text-primary">Tip: you can ask the dealer to reduce the price by $4.99 to cover it.</span>
          </p>
        </div>

        <ul className="mb-6 space-y-3">
          <Perk>Unblur this full report — score, disclosures & price check</Perk>
          <Perk>Scan unlimited contracts at any dealership</Perk>
          <Perk>Spot hidden fees before you sign</Perk>
        </ul>

        {user ? (
          <Button
            data-testid="paywall-pay-button"
            onClick={startCheckout}
            disabled={busy}
            className="h-12 w-full rounded-xl text-lg font-700"
          >
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to checkout…</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Unlock for $4.99</>
            )}
          </Button>
        ) : (
          <div className="space-y-2.5">
            <Button
              data-testid="paywall-login-button"
              onClick={login}
              variant="secondary"
              className="h-12 w-full rounded-xl border border-border bg-white text-lg font-700 text-slate-800 hover:bg-white/90"
            >
              <GoogleIcon className="mr-2.5" /> Continue with Google
            </Button>
            {appleEnabled && (
              <Button
                data-testid="paywall-apple-login-button"
                onClick={loginApple}
                className="h-12 w-full rounded-xl bg-black text-lg font-700 text-white hover:bg-black/90"
              >
                <AppleIcon className="mr-2.5" /> Continue with Apple
              </Button>
            )}
          </div>
        )}

        {user && (
          <p className="mt-3 text-center text-sm text-muted-foreground">
            Signed in as {user.email}
          </p>
        )}
      </div>
    </div>
  );
};
